import * as types from 'src/store/mutation-types'
import { ExpensesClient } from 'src/api/Expenses'
import { Notify } from 'quasar'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
// shape: [{ id, quantity }]
const state = {
  expenses: [],
  expensesMeta: null,
  expensesLoaded: false
}

// getters
const getters = {
  expenses: state => state.expenses,
  expensesMeta: state => state.expensesMeta,
  expensesLoaded: state => state.expensesLoaded
}

const paginationDefault = {
  page: 1,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'accounting_period',
  additionalIncludes: []
}

const actions = {
  listExpenses ({ commit, state }, { pagination = {}, filter = null }) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_EXPENSES_LOADED, false)

    new ExpensesClient()
      .include(['supplier', ...pagination.additionalIncludes])
      .sort(sort)
      .limit(pagination.rowsPerPage)
      .page(pagination.page)
      .filter(filter)
      .list()
      .then(response => {
        commit(types.SET_EXPENSES, response.data.data)
        commit(types.SET_EXPENSES_META, response.data.meta)
        commit(types.SET_EXPENSES_LOADED, true)
      })
      .then(() => Promise.resolve({ expenses: state.expenses, meta: state.expensesMeta }))
  },

  fetchNextExpensesChunk ({ commit, state }, { done, pagination = {}, filter = null }) {
    if (!state.expensesLoaded) {
      return done()
    } else if (!state.expensesMeta) {
      return done(true)
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    let currentPage = state.expensesMeta.pagination.current_page
    let totalPages = state.expensesMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      new ExpensesClient()
        .include(['supplier', ...pagination.additionalIncludes])
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .filter(filter)
        .list()
        .then(response => {
          let newExpenses = state.expenses.concat(response.data.data)

          commit(types.SET_EXPENSES, newExpenses)
          commit(types.SET_EXPENSES_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  commitExpense ({ commit, state }, { expenseId }) {
    new ExpensesClient()
      .commit(expenseId)
      .then(response => {
        commit(types.COMMIT_EXPENSE, { expenseId, date: response.data.data.committed_at })
        Notify.create({ message: i18n.t('expense_committed_success'), type: 'positive' })
      })
      .catch(createErrorFromResponse)
  },

  deleteExpense ({ commit, state }, { expenseId }) {
    const client = new ExpensesClient()
    client
      .include('payments')
      .get(expenseId)
      .then(response => {
        if (!response.data.data[0].payments) {
          return
        }

        return Promise.all(response.data.data[0].payments.data.map(p => {
          return client.destroyPayment(expenseId, p.id)
        }))
      })
      .then(() => {
        return client.destroy(expenseId)
      })
      .then(response => {
        commit(types.DELETE_EXPENSE, { expenseId })
        Notify.create({ message: i18n.t('expense_deleted_success'), type: 'positive' })
      })
      .catch(createErrorFromResponse)
  }
}

// mutations
const mutations = {
  [types.SET_EXPENSES] (state, expenses) {
    state.expenses = expenses
  },

  [types.SET_EXPENSES_META] (state, expensesMeta) {
    state.expensesMeta = expensesMeta
  },

  [types.SET_EXPENSES_LOADED] (state, loaded) {
    state.expensesLoaded = loaded
  },

  [types.COMMIT_EXPENSE] (state, { expenseId, date }) {
    const index = state.expenses.findIndex(expense => expense.id === expenseId)

    if (index !== -1) {
      state.expenses[index].committed_at = date
    }
  },

  [types.DELETE_EXPENSE] (state, { expenseId, date }) {
    const index = state.expenses.findIndex(expense => expense.id === expenseId)

    if (index !== -1) {
      state.expenses.splice(index, 1)
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
